import React, { useContext, useEffect, useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { RxCross2 } from 'react-icons/rx';
import feedaLogo from '../../assets/feedalogoWhite.svg';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useParams } from 'react-router-dom';
import sessionsService from '../../services/sessionsServices';
import { AuthContext } from '../../Context/authContext';
import { useContextHook } from 'use-context-hook';
import SourcesTab from './Sources';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import 'swiper/css';
import 'swiper/css/navigation';
import { ChatSectionStyled, ChatColumn, SearchResultCol } from './ChatSection.styles';
import Markdown from 'react-markdown';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import WaltChatHeader, { DEFAULT_WALT_AGENT } from './WaltChatHeader';
import WaltChatInput from './WaltChatInput';

function getAgentIconSrc(agent) {
  if (!agent?.icon) return null;
  if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://')) {
    return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
  }
  if (agent.icon.startsWith('data:')) return agent.icon;
  return `data:image/png;base64,${agent.icon}`;
}

const ChatSection = () => {
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const [activeTab, setActiveTab] = useState('Sources');
  const {
    selectedAgent,
    chatMessages,
    chatLoaderMessages,
    setChatmessages,
    setSessionAgent,
    sessionAgent,
    isActiveRef,
    setSessionID,
    updateAgent,
  } = useContext(TemporaryChatContext);
  const [selectedChatSources, setSelectedChatSources] = useState(null);
  const { id } = useParams();
  const tabs = ['Sources'];
  const hasSession = Boolean(id);
  const isTeamChat = selectedAgent?.mode === 'team' || sessionAgent?.mode === 'team';
  const hasMessages = chatMessages.length > 0;

  const getSessions = async sessionId => {
    try {
      setChatmessages([]);
      const aiResp = await sessionsService.getSessionHistory({
        user_id: user?.data?.ID,
        platform_id: 'chronolabs',
        agent_id: selectedAgent?.value || sessionAgent?.value || DEFAULT_WALT_AGENT.value,
        session_id: sessionId,
      });
      if (aiResp?.agent_name || aiResp?.agent_id) {
        const historyMode = aiResp?.mode || (aiResp?.session_mode === 'team' ? 'team' : 'agent');
        const historyAgent = {
          label: aiResp?.agent_name,
          value: aiResp?.agent_id,
          icon: aiResp?.icon,
          mode: historyMode,
        };

        setSessionAgent(prev => {
          if (prev?.mode === 'team') {
            return {
              ...prev,
              label: prev.label || historyAgent.label,
              icon: prev.icon || historyAgent.icon,
              mode: 'team',
              value: prev.value || historyAgent.value,
            };
          }
          return historyAgent;
        });

        const keepExistingTeam = historyMode === 'team' && selectedAgent?.mode === 'team';
        if (!keepExistingTeam && String(selectedAgent?.value) !== String(historyAgent.value)) {
          updateAgent(historyAgent);
        }
      }
      const formattedMessages = aiResp?.history?.flatMap(item => {
        const isSource = item?.sources ? item?.sources : [];
        const websearchSources = isSource?.filter(src => src.type === 'google_search');
        const fileSources = isSource?.filter(src => src.type === 'file');
        return [
          {
            human: item.human,
            ai: item?.ai,
            source: websearchSources.length > 0 ? websearchSources : [],
            files: fileSources.length > 0 ? fileSources : [],
            images: item?.images,
            isLoading: false,
          },
        ];
      });

      setChatmessages(formattedMessages);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id && isActiveRef.current) {
      getSessions(id);
      setSessionID(id);
    }
    if (!id) {
      // Fresh /chat landing: clear session leftover only. Keep the deliberately
      // selected agent/team so New Chat / messaging uses that agent_id (platformtl).
      setChatmessages([]);
      setSessionID(null);
      const isStaleTest = (selectedAgent?.label || '').toLowerCase() === 'test';
      if (isStaleTest || !selectedAgent?.value) {
        setSessionAgent(DEFAULT_WALT_AGENT);
        updateAgent(DEFAULT_WALT_AGENT);
      }
    }
  }, [id]);

  const handleTabClick = tab => {
    setActiveTab(tab);
  };

  function base64ToSrc(base64String, mimeType = 'image/png') {
    if (selectedAgent?.mode === 'team') {
      return selectedAgent?.icon;
    }
    if (!base64String?.icon) return null;
    if (base64String.icon.startsWith('http://') || base64String.icon.startsWith('https://')) {
      return base64String.icon;
    }
    return `data:${mimeType};base64,${base64String?.icon}`;
  }

  // On an open session, prefer the session's agent (from history / New Chat).
  // On fresh /chat, prefer the deliberately selected agent.
  const displayAgent = selectedAgent?.mode === 'team'
    ? selectedAgent
    : sessionAgent?.mode === 'team'
      ? sessionAgent
      : id && sessionAgent?.label
        ? sessionAgent
        : selectedAgent?.label
          ? selectedAgent
          : sessionAgent;
  const emptyStateIcon = useMemo(() => getAgentIconSrc(displayAgent), [displayAgent]);

  return (
    <ChatSectionStyled className={selectedChatSources !== null ? 'sources-active' : ''}>
      <ChatColumn className="poly-chat-layout">
        <WaltChatHeader />

        <div className="text-block poly-chat-messages">
          {!hasMessages ? (
            <div className="poly-chat-empty">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 overflow-hidden">
                {emptyStateIcon ? (
                  <img src={emptyStateIcon} alt="" className="w-full h-full object-cover" />
                ) : (
                  <MessageSquare className="w-8 h-8 text-primary" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-[var(--text-color)] mb-2 font-serif">
                {displayAgent?.label || 'Walt Chat'}
              </h3>
              <p className="text-sm text-muted max-w-md text-center">
                {isTeamChat
                  ? hasSession
                    ? 'Team chat is ready. Send a message to collaborate with this team.'
                    : `Click New Chat to start a session with ${displayAgent?.label || 'this team'}.`
                  : hasSession
                    ? 'Your conversational interface for agents. Send a message to begin.'
                    : displayAgent?.label
                      ? `Click New Chat to start a session with ${displayAgent.label}.`
                      : 'Select an agent, then click New Chat to create a session before messaging.'}
              </p>
            </div>
          ) : (
            <div className="poly-chat-thread">
              {chatMessages?.map((elem, ind) => (
                <div key={ind} className="min-w-0 max-w-full overflow-x-hidden">
                  {elem.human && (
                    <div className="heading user-text">
                      <div className="icon-box">{user?.first_name?.split('')[0]}</div>
                      <strong className="title whitespace-pre-line">{elem?.human}</strong>
                      {(elem?.source?.length > 0 || elem?.files?.length > 0) && (
                        <button
                          className="see-source-button"
                          type="button"
                          style={{ border: ind === selectedChatSources ? `2px solid var(--primary)` : undefined }}
                          onClick={() => setSelectedChatSources(ind)}>
                          see sources
                        </button>
                      )}
                    </div>
                  )}

                  <div className="heading">
                    <div className="icon-box icon-box--purple">
                      <img src={base64ToSrc(displayAgent) ?? feedaLogo} alt="Agent logo" />
                    </div>

                    {elem?.images && elem?.images?.length > 0 && elem?.images !== '' && elem?.isLoading === false && (
                      <div className="swiper-holder">
                        <Swiper
                          slidesPerView={4.2}
                          navigation={{
                            prevEl: '.swiper-button-prev',
                            nextEl: '.swiper-button-next',
                          }}
                          modules={[Navigation]}
                          breakpoints={{
                            320: { slidesPerView: 1.3 },
                            574: { slidesPerView: 2.2 },
                            1250: { slidesPerView: 2.8 },
                            1440: { slidesPerView: 3.2 },
                            1750: { slidesPerView: 4.2 },
                          }}>
                          {elem?.images?.map((data, index) => (
                            <SwiperSlide key={index}>
                              <a href={data?.origin_url} target="blank" className="img-wrap">
                                <img src={data?.image_url} alt="img" />
                              </a>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                        <div className="swiper-controls">
                          <button className="swiper-button-prev" type="button">
                            <IoIosArrowBack />
                          </button>
                          <button className="swiper-button-next" type="button">
                            <IoIosArrowForward />
                          </button>
                        </div>
                      </div>
                    )}

                    {elem.ai && elem.ai !== '' && elem?.isLoading === false && (
                      <div className="ai-message-content">
                        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                          {elem?.ai}
                        </Markdown>
                      </div>
                    )}

                    {elem?.isLoading && (
                      <>
                        {chatLoaderMessages?.newChunk?.tool_name ? (
                          <strong className="chat-loader-text">{chatLoaderMessages?.newChunk?.tool_name}...</strong>
                        ) : (
                          <div style={{ width: '100%' }}>
                            <strong className="chat-loader-text">Thinking...</strong>
                          </div>
                        )}
                      </>
                    )}

                    {chatMessages?.length === ind + 1 && chatLoaderMessages?.newChunk?.tool_name && (
                      <strong className="chat-loader-text">{chatLoaderMessages?.newChunk?.tool_name}...</strong>
                    )}

                    {!elem.ai &&
                      !elem?.isLoading &&
                      (user?.usage?.api_calls >= user?.plan?.request_limit ? (
                        <span className="error-text">❌ Limit exceeded. Please upgrade your plan.</span>
                      ) : (
                        <span className="error-text">Something went wrong !!!</span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="poly-chat-input">
          <div className="poly-chat-search">
            <WaltChatInput sessionId={id} />
          </div>
        </div>
      </ChatColumn>

      {selectedChatSources !== null && (
        <SearchResultCol>
          <div>
            <ul className="tablist">
              {tabs.map((tab, index) => (
                <li key={tab}>
                  <button
                    key={index}
                    type="button"
                    className={tab === activeTab ? 'active' : ''}
                    onClick={() => handleTabClick(tab)}>
                    {tab}
                  </button>
                </li>
              ))}
              <button type="button" className="source-close" title="close" onClick={() => setSelectedChatSources(null)}>
                <RxCross2 color="var(--danger)" />
              </button>
            </ul>
            <div className="tab-content">
              {activeTab === 'Sources' && <SourcesTab data={chatMessages[selectedChatSources]} />}
            </div>
          </div>
        </SearchResultCol>
      )}
    </ChatSectionStyled>
  );
};

export default ChatSection;
